/**
 * Conservazione dei token Keycloak per la durata della scheda.
 *
 * Serve a non perdere l'autenticazione a ogni ricaricamento della pagina
 * senza passare dall'iframe del `check-sso` (vedi `keycloak.config.ts`): i
 * token vengono restituiti alla libreria all'avvio, che li rinnova con una
 * normale richiesta al token endpoint. Il `check-sso` resta come seconda via,
 * quando non ci sono token o il rinnovo fallisce, e copre anche il browser
 * che blocca i cookie di terze parti, dove l'iframe non può funzionare.
 *
 * `sessionStorage` e non `localStorage`: il caso da risolvere è il
 * ricaricamento della pagina, e un token di rinnovo non deve sopravvivere alla
 * chiusura del browser. Il prezzo è che una nuova scheda riparte anonima.
 */
const STORAGE_KEY = 'nfarm_keycloak_tokens';

/** Margine entro cui un token è considerato già scaduto. */
const EXPIRY_SKEW_SECONDS = 15;

export interface StoredKeycloakTokens {
  token: string;
  refreshToken: string;
  idToken?: string;
}

/** Scadenza dichiarata dal token, o null se non leggibile. */
function readExpiry(jwt: string): number | null {
  try {
    const payload = jwt.split('.')[1];
    if (!payload) {
      return null;
    }
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const decoded = JSON.parse(atob(normalized)) as { exp?: unknown };
    return typeof decoded.exp === 'number' ? decoded.exp : null;
  } catch {
    return null;
  }
}

/**
 * Un token di rinnovo senza scadenza dichiarata viene accettato: alcuni realm
 * emettono token opachi, e il peggio che può accadere è un tentativo di
 * rinnovo respinto all'avvio.
 */
function isUsable(jwt: string): boolean {
  const exp = readExpiry(jwt);
  if (exp == null) {
    return true;
  }
  return exp - EXPIRY_SKEW_SECONDS > Math.floor(Date.now() / 1000);
}

export function storeKeycloakTokens(tokens: Partial<StoredKeycloakTokens>): void {
  if (!tokens.token || !tokens.refreshToken) {
    return;
  }
  try {
    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        token: tokens.token,
        refreshToken: tokens.refreshToken,
        idToken: tokens.idToken,
      })
    );
  } catch {
    // Spazio esaurito o modalità privata: si perde solo la comodità di
    // restare autenticati dopo un ricaricamento.
  }
}

export function clearKeycloakTokens(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Niente da fare: la chiave scade comunque con la scheda.
  }
}

/**
 * Token da restituire alla libreria all'avvio. Il token di rinnovo scaduto
 * viene scartato qui: passarlo produrrebbe un errore di inizializzazione
 * inutile, dato che l'esito sarebbe comunque anonimo.
 */
export function readStoredKeycloakTokens(): StoredKeycloakTokens | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as Partial<StoredKeycloakTokens>;
    if (!parsed.token || !parsed.refreshToken) {
      clearKeycloakTokens();
      return null;
    }

    if (!isUsable(parsed.refreshToken)) {
      clearKeycloakTokens();
      return null;
    }

    return {
      token: parsed.token,
      refreshToken: parsed.refreshToken,
      idToken: parsed.idToken,
    };
  } catch {
    clearKeycloakTokens();
    return null;
  }
}
